export const ROLES = {
  ADMIN: 'admin',
  TEAM_LEADER: 'teamLeader',
  USER: 'user',
  HOST: 'host',
  BOOKING: 'booking'
};

export const PERMISSIONS = {
  // Feature permissions by role
  canCreateOrders: {
    admin: true,
    teamLeader: true,
    user: true,
    host: false,        // Hosts cannot create orders
    booking: false      // Booking partners cannot create orders
  },
  canViewOrders: {
    admin: true,
    teamLeader: true,
    user: true,
    host: true,        // Hosts can view orders
    booking: true      // Booking partners can view orders
  },
  canBookDemos: {
    admin: true,
    teamLeader: true,
    user: true,
    host: true,       // Hosts can book demos
    booking: true     // Booking partners can book demos on behalf of team leaders
  },
  canUploadFiles: {
    admin: true,
    teamLeader: true,
    user: true,
    host: false,
    booking: false
  },
  canViewDocuments: {
    admin: true,
    teamLeader: true,
    user: true,
    host: false,       // Hosts cannot view company documents
    booking: false     // Booking partners cannot view company documents
  },
  canViewProductShowcase: {
    admin: true,
    teamLeader: true,
    user: true,
    host: true,
    booking: true
  },
  canViewAgentResources: {
    admin: true,
    teamLeader: true,
    user: true,
    host: false,
    booking: false
  },
  canViewAllData: {
    admin: true,        // Can see ALL demos/orders system-wide
    teamLeader: false,  // Can see ONLY team members' data (keeping this limited)
    user: false,        // Can see ONLY their own
    host: false,        // Hosts can see ONLY their own
    booking: false      // Booking partners can see ONLY their own
  },
  canAssignDemos: {
    admin: true,
    teamLeader: true,   // Can assign demos to team members
    user: false,
    host: false,
    booking: false
  },
  canViewMyUploads: {
    admin: true,
    teamLeader: true,
    user: true,
    host: false,
    booking: false
  },
  canAccessAdminPanel: {
    admin: true,
    teamLeader: false,
    user: false,
    host: false,       // Hosts cannot access admin panel
    booking: false     // Booking partners cannot access admin panel
  },
  canAccessTeamLeaderPanel: {
    admin: true,        // Admins can access all panels
    teamLeader: true,   // Team leaders access their team panel
    user: false,
    host: false,
    booking: false     // Booking partners cannot access team leader panel
  },
  canViewTeamData: {
    admin: true,
    teamLeader: true,
    user: true,  // Users can create agents, so they should be able to view team data
    host: false,
    booking: false
  },
  canCreateAgents: {
    admin: true,
    teamLeader: true,   // Team leaders can now create agents (same as user role)
    user: true,        // Users can create agents
    host: false,        // Hosts cannot create agents
    booking: false      // Booking partners cannot create agents
  },
  canAccessWhatsApp: {
    admin: true,
    teamLeader: true,
    user: true,
    host: true,
    booking: true
  },
  canViewDraftOrders: {
    admin: true,
    teamLeader: true,
    user: true,
    host: true,
    booking: true
  }
};

export const hasPermission = (role, permission) => {
  return PERMISSIONS[permission]?.[role] || false;
};

export const getPermissions = (role) => {
  return {
    canBookDemos: PERMISSIONS.canBookDemos?.[role] || false,
    canViewOrders: PERMISSIONS.canViewOrders?.[role] || false,
    canAccessAdminPanel: PERMISSIONS.canAccessAdminPanel?.[role] || false,
    canAccessTeamLeaderPanel: PERMISSIONS.canAccessTeamLeaderPanel?.[role] || false,
    canViewDocuments: PERMISSIONS.canViewDocuments?.[role] || false,
    canViewMyUploads: PERMISSIONS.canViewMyUploads?.[role] || false,
    canCreateAgents: PERMISSIONS.canCreateAgents?.[role] || false,
    canAccessWhatsApp: PERMISSIONS.canAccessWhatsApp?.[role] || false,
  };
};

export const getNavigationItems = (role, permissions, category = 'POP') => {
  const items = [];
  
  // Dashboard
  items.push({ 
    path: '/dashboard', 
    label: '🏠 Dashboard', 
    id: 'dashboard',
    requiredPermission: null  // Everyone can access dashboard
  });
  
  // Book Demo - HIDDEN for hosts
  if (permissions.canBookDemos && role !== 'host') {
    items.push({ 
      path: '/book-demo', 
      label: '📅 Book Demo', 
      id: 'book-demo',
      requiredPermission: 'canBookDemos'
    });
  }
  
  // Orders - HIDDEN for ALL users (available on dashboard)
  // Removed to reduce redundancy since orders are shown on dashboard
  
  // Demos - HIDDEN for ALL users (available on dashboard)  
  // Removed to reduce redundancy since demos are shown on dashboard
  
  // Admin Panel
  items.push({ 
    path: '/admin', 
    label: '⚙️ Admin Panel', 
    id: 'admin',
    requiredPermission: 'canAccessAdminPanel'  // Only admins
  });
  
  // Team Leader Panel - Only for PLSSS category
  if (category === 'PLSSS') {
    items.push({ 
      path: '/team-leader', 
      label: '\ud83d\udc65 Team Panel', 
      id: 'team-leader',
      requiredPermission: 'canAccessTeamLeaderPanel'  // Team leaders with PLSSS category
    });
  }
  
  // My Feedback
  items.push({ 
    path: '/my-feedback', 
    label: '💬 My Feedback', 
    id: 'my-feedback',
    requiredPermission: null  // Everyone can access their feedback
  });
  
  // Product Showcase - for ALL logged-in users
  items.push({ 
    path: '/product-showcase', 
    label: '🛍️ Product Showcase', 
    id: 'product-showcase',
    requiredPermission: 'canViewProductShowcase'
  });

  // Draft Orders - admin and team leaders only
  items.push({
    path: '/draft-orders',
    label: '📋 Draft Orders',
    id: 'draft-orders',
    requiredPermission: 'canViewDraftOrders'
  });

  // Host Gifts - for ALL users except hosts
  if (role !== 'host') {
    items.push({
      path: '/host-gifts',
      label: '🎁 Hosting Gifts',
      id: 'host-gifts',
      requiredPermission: null
    });
  }
  
  // Create Agent - only for admin, user, and teamLeader roles
  if (permissions.canCreateAgents) {
    let label = '👤 Create Booking Partner'; // default
    
    if (role === 'admin' || role === 'teamLeader') {
      label = '👤 Create User';
    } else if (role === 'user') {
      label = '👤 Create Booking Partner';
    }
    
    items.push({ 
      path: '#createagent', 
      label: label,
      id: 'createagent',
      requiredPermission: 'canCreateAgents',
      action: 'openCreateAgentModal' // Special action for modal
    });
  }
  
  return items.filter(item => 
    !item.requiredPermission || hasPermission(role, item.requiredPermission)
  );
};
